import { FC, useEffect, useState } from 'react';
import { InlineNotification, TextArea } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import 'components/inline-edit/ibm-products-config';
import type { StagedWell } from 'types/well-workspace';
import type { WellStatusVerdictSetter } from '../hooks';
import styles from './well-status-verdict-panel.module.scss';

export type VerdictKind = 'correct' | 'incorrect';

interface Props {
  /** The verdict being set, fixed by which card icon opened the panel. Null = closed. */
  kind: VerdictKind | null;
  /** The scoped well, for its name and its freeze item. */
  well: StagedWell | undefined;
  setter: WellStatusVerdictSetter;
  onClose: () => void;
}

/**
 * Mark as Correct / Mark as Incorrect — the decision panel behind the Well Status card's
 * two verdict icons.
 *
 * Mirrors the Type Curve and Inventory workspaces' decision panels: the verdict is fixed
 * by which control opened it (no in-panel toggle), and a reason is required before Save.
 * The chrome comes from the same `SidePanel` the Edit Well Status panel uses, so the
 * page's two right-hand panels open, push and close identically.
 *
 * Marking a well correct and then promoting is what freezes its Well Status against
 * system ingest — which is why the reason is worth capturing rather than assumed.
 * @param {Props} props The verdict kind, the scoped well, the setter and the close handler.
 * @return {JSX.Element | null} The panel, or nothing with no well scoped.
 */
export const WellStatusVerdictPanel: FC<Props> = ({
  kind,
  well,
  setter,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const [blurred, setBlurred] = useState(false);

  // Re-opening with the other verdict starts clean, so a reason written for "correct"
  // cannot ride along into an "incorrect".
  useEffect(() => {
    setReason('');
    setBlurred(false);
    setter.reset();
    // `setter` is rebuilt every render by useMutation; depending on it would clear the
    // box on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, well?.uwi]);

  const verificationItemId = well?.wellStatusVerdict?.verificationItemId;
  const isCorrect = kind === 'correct';
  const trimmed = reason.trim();
  const missingReason = blurred && trimmed.length === 0;

  const handleSave = () => {
    if (!trimmed) {
      setBlurred(true);

      return;
    }
    if (!verificationItemId || !kind) return;

    setter.set({ verificationItemId, verdict: kind, reason: trimmed }, onClose);
  };

  return (
    <SidePanel
      open={kind !== null && Boolean(well)}
      onRequestClose={onClose}
      title={isCorrect ? 'Mark as Correct' : 'Mark as Incorrect'}
      subtitle={`Why are you marking ${well?.name ?? 'this well'}'s status as ${
        isCorrect ? 'correct' : 'incorrect'
      }?`}
      slideIn
      selectorPageContent="#well-workspace-main"
      placement="right"
      size="md"
      animateTitle={false}
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: setter.saving ? 'Saving…' : 'Save',
          onClick: handleSave,
          disabled: setter.saving || trimmed.length === 0,
        },
      ]}
    >
      <div className={styles.body}>
        {setter.error && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Save failed"
            subtitle={setter.error}
          />
        )}
        <TextArea
          id="well-status-verdict-reason"
          labelText="Reason"
          placeholder={`Explain why this well's status is ${
            isCorrect ? 'correct' : 'incorrect'
          }.`}
          value={reason}
          invalid={missingReason}
          invalidText={missingReason ? 'A reason is required.' : undefined}
          disabled={setter.saving}
          className={styles.field}
          onChange={(event) => setReason(event.target.value)}
          onBlur={() => setBlurred(true)}
        />
      </div>
    </SidePanel>
  );
};
